import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Mail, ArrowRight, ShieldCheck, RefreshCw } from "lucide-react";

const GATE_KEY = "atom.visitor";

export function getVisitor() {
  try { return JSON.parse(localStorage.getItem(GATE_KEY) || "null"); } catch { return null; }
}

export default function PinGate() {
  const [stage, setStage] = useState("email"); // "email" | "pin"
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputs = useRef([]);
  const nav = useNavigate();

  // Already verified? skip gate
  useEffect(() => {
    const v = getVisitor();
    if (v && v.token) nav("/login", { replace: true });
  }, [nav]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const requestPin = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes("@")) { setError("Please enter a valid email"); return; }
    setLoading(true); setError(""); setInfo("");
    try {
      const r = await api.post("/auth/pin/request", { email, name, company });
      if (r.data.ok) {
        setStage("pin");
        setInfo(`PIN sent to ${email}. Check your inbox (and spam folder).`);
        setResendCooldown(30);
        setTimeout(() => inputs.current[0]?.focus(), 100);
      } else if (r.data.dev_pin) {
        // Email delivery failed — still let demo proceed
        setStage("pin");
        setInfo(`Your PIN is ${r.data.dev_pin}. (We couldn't reach your inbox just now — please use this code.)`);
        setTimeout(() => inputs.current[0]?.focus(), 100);
      } else {
        setError(r.data.error || "Could not send PIN");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send PIN");
    } finally { setLoading(false); }
  };

  const onPinChange = (i, val) => {
    const v = val.replace(/\D/g, "").slice(0, 1);
    const next = [...pin]; next[i] = v; setPin(next);
    if (v && i < 5) inputs.current[i + 1]?.focus();
    if (next.every(c => c.length === 1)) {
      submitPin(next.join(""));
    }
  };

  const onPaste = (e) => {
    e.preventDefault();
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (txt.length === 6) {
      const arr = txt.split("");
      setPin(arr);
      submitPin(txt);
    }
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !pin[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const submitPin = async (code) => {
    if (code.length !== 6) return;
    setLoading(true); setError(""); setInfo("");
    try {
      const r = await api.post("/auth/pin/verify", { email, pin: code });
      if (r.data.ok) {
        localStorage.setItem(GATE_KEY, JSON.stringify(r.data.visitor));
        nav("/login", { replace: true });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not verify PIN");
      setPin(["", "", "", "", "", ""]);
      setTimeout(() => inputs.current[0]?.focus(), 100);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* LEFT — brand panel */}
      <div className="hidden lg:flex flex-1 flex-col justify-between px-16 py-12 relative" style={{ background: "linear-gradient(180deg, #F4F1E8 0%, #EFEBDF 100%)" }}>
        <div className="flex items-center gap-2.5 z-10">
          <div className="w-7 h-7 rounded-lg bg-amber flex items-center justify-center shadow-[0_2px_6px_rgba(245,154,45,0.4)]">
            <div className="grid grid-cols-3 gap-[1.5px] w-3 h-3">
              {[1,1,1, 1,0,1, 1,1,1].map((v,i) => <span key={i} className={`w-[3px] h-[3px] ${v ? 'bg-white' : 'bg-transparent'}`} />)}
            </div>
          </div>
          <span className="display text-[16px] font-semibold tracking-tight text-ink">Atom <span className="text-ink-muted font-normal">AML</span></span>
        </div>

        <div className="max-w-[560px] z-10">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/70 border border-line rounded-full backdrop-blur-sm text-[10px] text-ink-muted mb-6">
            <ShieldCheck size={11} strokeWidth={1.5} className="text-risk-low" />
            Email-verified access · audit-grade workspace
          </div>
          <h1 className="display font-semibold tracking-[-0.04em] text-ink leading-[1.02]" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
            Before we let you in,{" "}
            <span className="italic font-serif" style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 500 }}>tell us who you are</span>
          </h1>
          <p className="text-[15px] text-ink-muted mt-6 max-w-[440px] leading-relaxed">
            Atom AML is a live financial-crime compliance workspace. We send a one-time PIN to your email so we know who's evaluating the platform.
          </p>

          {/* Step-by-step walkthrough */}
          <div className="mt-7 space-y-3 max-w-[440px]">
            {[
              { n: 1, t: "Enter your work email", d: "We'll send a 6-digit PIN to that address. Use a real inbox — the PIN expires in 10 minutes." },
              { n: 2, t: "Type the PIN here", d: "Paste or key in the code. You'll be taken straight to the compliance workspace." },
              { n: 3, t: "Sign in with a pre-loaded persona", d: "On the next page, we've pre-filled a Chief Compliance Officer account. Just click Log in — no password to remember." },
              { n: 4, t: "Explore freely", d: "Try the dashboard, drill into a state on the Nigeria map, open a case, ask Atom anything from the chat dock." },
            ].map(s => (
              <div key={s.n} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-ink text-white flex items-center justify-center text-[11px] font-medium shrink-0 mt-0.5">{s.n}</div>
                <div>
                  <div className="text-[13px] font-medium text-ink">{s.t}</div>
                  <div className="text-[11.5px] text-ink-muted leading-relaxed">{s.d}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-7 flex items-center gap-2 text-[11px] text-ink-subtle">
            <span className="w-1.5 h-1.5 rounded-full bg-risk-low" />
            <span>PIN expires in 10 minutes · 5 attempt limit · entirely safe to demo</span>
          </div>
        </div>

        <div className="flex items-center gap-2 z-10">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/70 border border-line rounded-full backdrop-blur-sm text-[10px] text-ink-muted">
            <span className="w-1.5 h-1.5 rounded-full bg-risk-low" /> Atom Foundation Model
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/70 border border-line rounded-full backdrop-blur-sm text-[10px] text-ink-muted">
            <span className="w-1.5 h-1.5 rounded-full bg-risk-low" /> CBN-aligned · ISO 27001
          </span>
        </div>
      </div>

      {/* RIGHT — form */}
      <div className="flex-1 lg:flex-none lg:w-[520px] flex flex-col px-8 lg:px-12 py-10 relative">
        <div className="flex justify-center mb-12 mt-4">
          <div className="display text-[28px] font-bold tracking-[-0.03em] text-ink" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
            Atom<span className="text-amber">AML</span>
          </div>
        </div>

        <div className="w-full max-w-[360px] mx-auto flex-1 flex flex-col">
          {stage === "email" && (
            <form onSubmit={requestPin} className="space-y-3">
              <div>
                <div className="display text-[22px] font-semibold tracking-tight">Verify your email</div>
                <div className="text-[12px] text-ink-muted mt-1">We'll send you a 6-digit PIN to get in.</div>
              </div>

              <div className="space-y-2.5 pt-3">
                <div className="relative">
                  <Mail size={13} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-subtle" />
                  <input
                    data-testid="gate-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourbank.com"
                    required
                    autoFocus
                    className="w-full bg-bg-warm border border-line pl-10 pr-3 py-3 text-[13px] rounded-full focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber transition-all"
                  />
                </div>
                <input
                  data-testid="gate-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full bg-bg-warm border border-line px-4 py-3 text-[13px] rounded-full focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber transition-all"
                />
                <input
                  data-testid="gate-company"
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Company / institution (optional)"
                  className="w-full bg-bg-warm border border-line px-4 py-3 text-[13px] rounded-full focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber transition-all"
                />
              </div>

              {error && <div className="text-[11px] text-risk-veryhigh bg-risk-veryhigh/8 px-3 py-2 rounded-md">{error}</div>}

              <button
                type="submit"
                data-testid="gate-send-pin"
                disabled={loading}
                className="group w-full bg-ink text-white font-medium py-3.5 text-[13px] rounded-full mt-3 flex items-center justify-center gap-2.5 transition-all hover:bg-navy hover:shadow-[0_8px_20px_rgba(11,43,92,0.18)] disabled:opacity-60"
              >
                {loading ? "Sending PIN…" : (<>Send PIN to my email <ArrowRight size={13} strokeWidth={2} /></>)}
              </button>

              <div className="text-[10px] text-ink-subtle text-center pt-4">
                By continuing, you agree to receive a one-time verification email from Atom AML.
              </div>
            </form>
          )}

          {stage === "pin" && (
            <div className="space-y-3">
              <div>
                <div className="display text-[22px] font-semibold tracking-tight">Enter your 6-digit PIN</div>
                <div className="text-[12px] text-ink-muted mt-1">Sent to <span className="font-medium text-ink">{email}</span></div>
              </div>

              {info && <div className="text-[11px] text-risk-low bg-risk-low/8 px-3 py-2 rounded-md flex items-center gap-1.5"><ShieldCheck size={12} strokeWidth={1.5} /> {info}</div>}

              <div className="flex gap-2 justify-between pt-2" onPaste={onPaste}>
                {pin.map((v, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputs.current[i] = el)}
                    data-testid={`gate-pin-${i}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={v}
                    onChange={(e) => onPinChange(i, e.target.value)}
                    onKeyDown={(e) => onKeyDown(i, e)}
                    className="w-12 h-14 text-center bg-bg-warm border border-line text-[20px] font-medium rounded-xl focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber tabular"
                  />
                ))}
              </div>

              {error && <div className="text-[11px] text-risk-veryhigh bg-risk-veryhigh/8 px-3 py-2 rounded-md">{error}</div>}

              <button
                onClick={() => submitPin(pin.join(""))}
                disabled={loading || pin.join("").length !== 6}
                data-testid="gate-verify-pin"
                className="w-full bg-ink text-white font-medium py-3.5 text-[13px] rounded-full mt-2 flex items-center justify-center gap-2.5 transition-all hover:bg-navy disabled:opacity-50"
              >
                {loading ? "Verifying…" : "Verify and continue"}
              </button>

              <div className="flex items-center justify-between text-[11px] pt-1">
                <button onClick={() => setStage("email")} className="text-ink-muted hover:text-ink underline underline-offset-2">Use a different email</button>
                <button
                  onClick={requestPin}
                  disabled={resendCooldown > 0 || loading}
                  data-testid="gate-resend"
                  className="text-amber-600 hover:underline disabled:text-ink-subtle disabled:no-underline inline-flex items-center gap-1"
                >
                  <RefreshCw size={10} strokeWidth={1.5} /> {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend PIN"}
                </button>
              </div>
            </div>
          )}

          <div className="mt-auto pt-8 text-center text-[11px] text-ink-subtle">
            Secured · CBN-aligned · NDPA 2023 · ISO 27001
          </div>
        </div>
      </div>
    </div>
  );
}
